import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../Components/Atoms/Button';
import Typography from '../../../Components/Atoms/Typography';
import TextField from '../../../Components/Atoms/TextField';
import ShowPwdTextfield from '../../../Components/Molecules/ShowPwdTextfield.tsx';
import { GoogleIcon } from '../../../Components/Atoms/Icons';
import InvitationLayout from '../../Components/InvitationLayout';
import { AuthContext, JoinMode, JoinResult } from '../../../Contexts/AuthContext';
import { PROBLEM_TEXT } from '../../../utils/requestStatus';

export type SignUpProps = {
  invitationId: string;
  navigate: (path: string) => void;
};

const EMAIL = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const MIN_PASSWORD = 8;

type Form = { name: string; email: string; password: string };

const validate = (mode: JoinMode, form: Form) => {
  const errors: Record<string, string | null> = {};
  if (mode === 'signUp' && form.name.trim().length < 3) errors.name = 'Please enter your full name';
  if (!EMAIL.test(form.email.trim())) errors.email = 'Please enter a valid email';
  // Only a new account has to meet the registration rule; an existing one keeps its password.
  if (mode === 'signUp' && form.password.length < MIN_PASSWORD) {
    errors.password = 'Password should have at least 8 characters';
  }
  if (mode === 'signIn' && !form.password) errors.password = 'Please enter your password';
  return errors;
};

const SignUp = ({ invitationId, navigate }: SignUpProps) => {
  const { t } = useTranslation('helpersAndErrors');
  const { joinWithEmail, joinWithGoogle, invitation } = useContext(AuthContext);
  const [mode, setmode] = useState<JoinMode>('signUp');
  const [form, setform] = useState<Form>({ name: '', email: '', password: '' });
  const [errors, seterrors] = useState<Record<string, string | null>>({});
  const [showPwd, setshowPwd] = useState(false);
  const [problem, setproblem] = useState<string | null>(null);
  const [busy, setbusy] = useState(false);
  const running = useRef(false);

  const finish = (result: JoinResult) => {
    if ('problem' in result) {
      setproblem(PROBLEM_TEXT[result.problem]);
      return;
    }
    navigate(`/${invitationId}/${result.next === 'name' ? 'sendRequest' : 'waitingForAcceptance'}`);
  };

  // One attempt at a time: a repeated submit must not register twice.
  const run = async (attempt: () => Promise<JoinResult>) => {
    if (running.current) return;
    running.current = true;
    setbusy(true);
    setproblem(null);
    try {
      finish(await attempt());
    } finally {
      running.current = false;
      setbusy(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(mode, form);
    seterrors(found);
    if (Object.keys(found).length) return;
    run(() => joinWithEmail({ mode, email: form.email.trim(), password: form.password, name: form.name }));
  };

  const onChange = ({ name, value }: { name: string; value: string }) => {
    setform((f) => ({ ...f, [name]: value }));
    seterrors((er) => ({ ...er, [name]: null }));
  };

  const onBlur = ({ name }: { name: string; value: string }) => {
    const error = validate(mode, form)[name] ?? null;
    seterrors((er) => ({ ...er, [name]: error }));
    return !!error;
  };

  const switchMode = (next: JoinMode) => {
    setmode(next);
    seterrors({});
    setproblem(null);
  };

  return (
    <InvitationLayout>
      <Typography variant='h3' className='invitation-title' translation={null}>
        {t('Work at {{restaurant}}', { restaurant: invitation.restaurantName ?? '' })}
      </Typography>
      <Typography variant='text-2' color='subdued' className='invitation-lead' translation='helpersAndErrors'>
        The restaurant has to approve your access before you can start.
      </Typography>

      <div className='invitation-mode' role='group' aria-label={t('Account')}>
        <Button
          variant={mode === 'signUp' ? 'primary' : 'outlined'}
          aria-pressed={mode === 'signUp'}
          onClick={() => switchMode('signUp')}
          translation='helpersAndErrors'
        >
          New account
        </Button>
        <Button
          variant={mode === 'signIn' ? 'primary' : 'outlined'}
          aria-pressed={mode === 'signIn'}
          onClick={() => switchMode('signIn')}
          translation='helpersAndErrors'
        >
          I already have an account
        </Button>
      </div>

      {problem && (
        <div className='invitation-banner tone-error' role='alert'>
          <Typography variant='text-3' translation='helpersAndErrors'>
            {problem}
          </Typography>
        </div>
      )}

      <form className='invitation-form' onSubmit={onSubmit} noValidate>
        {mode === 'signUp' && (
          <TextField
            fullWidth
            className='form-fields'
            name='name'
            label='Enter name'
            autoComplete='name'
            value={form.name}
            onChange={(e) => onChange(e.target)}
            onBlur={(e) => onBlur(e.target)}
            error={!!errors.name}
            helperText={errors.name ? t(errors.name) : ''}
          />
        )}
        <TextField
          fullWidth
          className='form-fields'
          name='email'
          type='email'
          label='Enter email'
          autoComplete='username'
          value={form.email}
          onChange={(e) => onChange(e.target)}
          onBlur={(e) => onBlur(e.target)}
          error={!!errors.email}
          helperText={errors.email ? t(errors.email) : ''}
        />
        <ShowPwdTextfield
          state={{ errors, informations: form }}
          onBlur={onBlur}
          onChange={onChange}
          showPwd={showPwd}
          setshowPwd={setshowPwd}
          autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
        />
        <div className='invitation-actions'>
          <Button variant='primary' type='submit' fullWidth loading={busy} disabled={busy} translation='helpersAndErrors'>
            {mode === 'signUp' ? 'Create account and send request' : 'Sign in and send request'}
          </Button>
        </div>
      </form>

      <div className='invitation-or'>
        <Typography variant='text-3' color='subdued' translation='common'>
          or
        </Typography>
      </div>

      <Button
        variant='outlined'
        fullWidth
        disabled={busy}
        startIcon={() => <GoogleIcon />}
        onClick={() => run(joinWithGoogle)}
        translation='helpersAndErrors'
      >
        Continue with Google
      </Button>
    </InvitationLayout>
  );
};

export default SignUp;
